import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import style from './FieldSet.css'

let fallbackLegendId = 0

/**
 * Field Set groups related fields under a [Field Legend](/components/field-legend/)
 * for semantic, accessible grouping. It exposes `role="group"` named by its
 * legend; wrap a [Field Group](/components/field-group/) inside.
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @slot - Default slot for a [Field Legend](/components/field-legend/) and a [Field Group](/components/field-group/).
 */
@customElement('nord-field-set')
export default class FieldSet extends LitElement {
  /**
   * Disables every field in the set. Each enclosed [Field](/components/field/)
   * reads this and cascades it to its control.
   */
  @property({ reflect: true, type: Boolean }) disabled = false

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'group')
    }
  }

  protected createRenderRoot() {
    return this
  }

  protected firstUpdated() {
    this.linkLegend()
  }

  // Name the group with its legend via aria-labelledby (the light-DOM
  // equivalent of a native `<fieldset>`/`<legend>` relationship).
  private linkLegend() {
    const legend = this.querySelector(':scope > nord-field-legend')
    if (!legend) {
      return
    }
    if (!legend.id) {
      legend.id = `n-field-set-legend-${fallbackLegendId++}`
    }
    this.setAttribute('aria-labelledby', legend.id)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-field-set': FieldSet
  }
}
